import React, { useState, useEffect } from 'react';
import { FaPencil, FaPlus, FaTrashArrowUp, FaFolder } from 'react-icons/fa6';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import { useGetCategoryListQuery, useDeleteCategoryMutation } from './../../../../Services/CategoryService';
import Breadcrumb from '../../../../Components/BreadCrumb';
import { Table, TableBody, TableCell, TableContainer, TablePagination, TableHead, TableRow, Typography, Paper, useTheme, Avatar } from '@mui/material';
import { common } from '@mui/material/colors';
import OffCanvas from '../../../../Components/OffCanvas';
import ChipStatus from '../../../../Components/ChipStatus';
import { useConfirmation } from '../../../../Components/ConfirmationDialog';
import { showSnackbar } from '../../../../Redux/Slices/NotificationSlice';
import { useDispatch } from 'react-redux';
import { FiPlus } from 'react-icons/fi';
import  AddMembership from './AddMembership';
import {MembershipFormInputs} from "../../../../Helpers/Interfaces/SupplierType";

const breadcrumbItems = [
  // { label: 'Dashboard', path: '/dashboard' },
  { label: 'Supplier Lavel' },
];

const SupplierLavel: React.FC = () => {
  const form:MembershipFormInputs={_id:"", supplierLevel: "", description: "", value: 0, isActive: false };
  const [isAddSupplierOpen, setIsAddSupplierOpen] = useState(false);
  const [tempUpdateData, setTempUpdateData] = useState<MembershipFormInputs>(form);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filteredData, setFilteredData] = useState<any[]>([]);

  const { data: categoryList, error, isLoading } = useGetCategoryListQuery();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { requestConfirmation } = useConfirmation();
  const [deleteCategory] = useDeleteCategoryMutation();

  useEffect(() => {
    if (categoryList?.length) {
      setFilteredData(categoryList);
    }
  }, [categoryList]);

  const handleCategoryToggle = () => {
    setTempUpdateData(form);
    setIsAddSupplierOpen(!isAddSupplierOpen);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDelete = (id: string) => {
    requestConfirmation({
      title: 'Delete Category',
      message: 'Are you sure you want to delete this category?',
      onConfirm: async () => {
        try {
          await deleteCategory({id, isDelete:true});
          setFilteredData(prev => prev.filter(item => item._id !== id));
          dispatch(showSnackbar({
            message: 'Category deleted successfully',
            severity: 'success'
          }));
        } catch (error) {
          dispatch(showSnackbar({
            message: 'Failed to delete category',
            severity: 'error'
          }));
        }
      }
    });
  };

  // Apply offline pagination
  const paginatedData = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <div className="container-lg container-md container-sm">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div style={{ flex: 1 }}>
          <Breadcrumb items={breadcrumbItems} name={'Supplier Lavel'} />
        </div>
        <Button
          variant="contained"
          onClick={() => setIsAddSupplierOpen(true)}
          startIcon={<FiPlus />}
        >Add Membership Tier</Button>
      </div>

      <div style={{ display: 'flex', gap: '20px', marginTop: '20px' }}>
        <Paper style={{ flex: 2 }} elevation={3}>
          <TableContainer>
            <Table size={'small'}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Icons</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Parent&nbsp;Category</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Category&nbsp;Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="textSecondary">Loading categories...</Typography>
                    </TableCell>
                  </TableRow>
                ) : error ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="error">Failed to load categories.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedData.map((item, index) => (
                    <TableRow key={item.id || index}>
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>
                        {item?.icon ? (
                          <Avatar alt="Category Icon" src={item.icon} variant="rounded" sx={{ width: 32, height: 32 }} />
                        ) : <Avatar sx={{ width: 32, height: 32 }}><FaFolder /></Avatar>}
                      </TableCell>
                      <TableCell>{item.parentCategoryName}</TableCell>
                      <TableCell>{item.categoryName}</TableCell>
                      <TableCell>
                        <Typography variant={'inherit'} sx={{
                          display: '-webkit-box',
                          overflow: 'hidden',
                          WebkitBoxOrient: 'vertical',
                          WebkitLineClamp: 1,
                        }}>{item.description}</Typography>
                      </TableCell>
                      <TableCell>
                        <ChipStatus status={item.isActive ? 'active' : 'error'} label={item.isActive ? 'Active' : 'InActive'} />
                      </TableCell>
                      <TableCell>
                        <IconButton color="primary" sx={{ color: theme.palette.error.main }} onClick={() => handleDelete(item._id)}>
                          <FaTrashArrowUp size={15} />
                        </IconButton>
                        <IconButton
                          color="primary"
                          sx={{ color: theme.palette.warning.main, ml: 0.1 }}
                          onClick={() => {
                            handleCategoryToggle();
                            setTempUpdateData(item);
                          }}>
                          <FaPencil size={15} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                borderTop: `1px solid ${theme.palette.divider}`,
              }}
            />
          </TableContainer>
        </Paper>
      </div>

      <OffCanvas open={isAddSupplierOpen} onClose={handleCategoryToggle} title="Add New Category" width={400}>
        <AddMembership open={isAddSupplierOpen} updateData={tempUpdateData} onClose={handleCategoryToggle} /> 
        {/* <AddCategory open={isAddSupplierOpen} updateData={tempUpdateData} onClose={handleCategoryToggle} /> */}
      </OffCanvas>
    </div>
  );
};

export default SupplierLavel;